\<template>
    <table class="soil-report-results" v-if="has_recs">
        <thead>
            <tr>
                <th rowspan="3" class="media-print-hidden">&nbsp;</th>
                <th rowspan="3">SAMPLE<br>ID</th>
                <th colspan="2" class="border-bottom-0">CROP</th>
                <th rowspan="3">YIELD<br>GOAL</th>
                <th colspan="4">SOIL AMENDMENTS</th>
                <th :colspan="show_crop_removal ? 2 : 1" class="border-bottom-0">NITROGEN</th>
                <th :colspan="show_crop_removal ? 2 : 1" class="border-bottom-0">PHOSPHATE</th>
                <th :colspan="show_crop_removal ? 2 : 1" class="border-bottom-0">POTASH</th>
                <th :colspan="show_crop_removal ? 2 : 1" class="border-bottom-0">MAGNESIUM</th>
                <th :colspan="show_crop_removal ? 2 : 1" class="border-bottom-0">SULFUR</th>
                <th :colspan="show_crop_removal ? 2 : 1" class="border-bottom-0">ZINC</th>
                <th class="border-bottom-0">MANGANESE</th>
                <th class="border-bottom-0">IRON</th>
                <th class="border-bottom-0">COPPER</th>
                <th class="border-bottom-0">BORON</th>
            </tr>

            <tr>
                <th rowspan="2" class="align-bottom">INTENDED</th>
                <th rowspan="2" class="align-bottom">PREVIOUS</th>
                <!-- Adjust for Missouri -->
                <th rowspan="2" class="align-bottom" v-if="getUSAState() == 'MO'">LIME<br>LBS/A<br>of ENM</th>
                <th rowspan="2" class="align-bottom" v-else>LIME<br>LBS/A<br>of CaCO<sub>3</sub></th>
                <th rowspan="2" class="align-bottom" v-if="getUSAState() == 'MO'"></th>
                <th rowspan="2" class="align-bottom" v-else>LIME<br>TONS/A<br>90% ECCE</th>
                <th rowspan="2" class="align-bottom">GYPSUM<br>TONS/A</th>
                <th rowspan="2" class="align-bottom">ELEMENTAL<br>SULFUR<br>LBS/A</th>
                <th :colspan="show_crop_removal ? 2 : 1" class="border-top-0" :class="{ 'border-bottom-0': show_crop_removal }">N</th>
                <th :colspan="show_crop_removal ? 2 : 1" class="border-top-0" :class="{ 'border-bottom-0': show_crop_removal }">P<sub>2</sub>O<sub>5</sub></th>
                <th :colspan="show_crop_removal ? 2 : 1" class="border-top-0" :class="{ 'border-bottom-0': show_crop_removal }">K<sub>2</sub>O</th>
                <th :colspan="show_crop_removal ? 2 : 1" class="border-top-0" :class="{ 'border-bottom-0': show_crop_removal }">Mg</th>
                <th :colspan="show_crop_removal ? 2 : 1" class="border-top-0" :class="{ 'border-bottom-0': show_crop_removal }">S</th>
                <th :colspan="show_crop_removal ? 2 : 1" class="border-top-0" :class="{ 'border-bottom-0': show_crop_removal }">Zn</th>
                <th rowspan="2" class="border-top-0">Mn</th>
                <th rowspan="2" class="border-top-0">Fe</th>
                <th rowspan="2" class="border-top-0">Cu</th>
                <th rowspan="2" class="border-top-0">B</th>
            </tr>

            <tr>
                <template v-for="index in 6">
                    <th class="align-bottom border-top-0 border-right-0" v-show="show_crop_removal">CR</th>
                    <th class="align-bottom border-top-0 border-left-0" v-show="show_crop_removal">REC</th>
                </template>
            </tr>
        </thead>

        <tbody>
            <template v-for="(sample, sample_index) in samples" v-if="sample.recs && sample.recs.length > 0">
                <tr v-for="(rec, rec_index) in sample.recs" :class="{ darken: sample_index % 2 == 1 }">
                    <td class="media-print-hidden" :class="{ 'border-bottom-2': sample_index == samples.length-1 }" :rowspan="sample.recs.length" v-if="rec_index == 0">
                        <i class="fa fa-circle cursor-pointer text-warning"
                           :class="{ 'invisible': !sample.client_id }"
                           @click="edit_recs(sample_index)"
                        ></i>
                    </td>
                    <td class="text-center" :class="{ 'border-bottom-2': sample_index == samples.length-1 }" :rowspan="sample.recs.length" v-if="rec_index == 0">{{ sample.client_id }}</td>
                    <td>{{ rec.crop.name }}</td>
                    <td>{{ rec.previous.name }}</td>
                    <td>{{ rec.yield }}</td>
                    <!-- Adjust for Missouri -->
                    <td v-if="getUSAState() == 'MO'">{{ rec.limepounds * .4 | Recommendation }}</td>
                    <td v-else>{{ rec.limepounds | Recommendation }}</td>
                    <td v-if="getUSAState() == 'MO'">-</td>
                    <td v-else>{{ rec.limetons | Recommendation }}</td>
                    <td>{{ rec.gyptons | Recommendation }}</td>
                    <td>{{ rec.elesulf | Recommendation }}</td>

                    <template v-for="element in ['n','p2o5','k2o','mg','s','zn']">
                        <td v-show="show_crop_removal">{{ rec | CropRemoval(element) }}</td>
                        <td>{{ rec[element] | Recommendation }}</td>
                    </template>

                    <td>{{ rec.mn | Recommendation }}</td>
                    <td>{{ rec.fe | Recommendation }}</td>
                    <td>{{ rec.cu | Recommendation }}</td>
                    <td>{{ rec.b | Recommendation }}</td>
                </tr>
            </template>
        </tbody>
    </table>
</template>

<script type="text/ecmascript-6">
    export default{
        props: {
            samples: { type: Array, default() { return [] }},
            show_crop_removal: { type: Boolean, default: false }
        },
        data () {
            return {

            }
        },
        computed: {
            has_recs() {
                return this.samples.filter(sample => sample.recs && sample.recs.length > 0).length > 0
            }
        },
        filters: {
            Recommendation (rec) {
                return rec > 0 ? rec : '-';
            },
            CropRemoval (rec, element) {
                let crop_removal = _.has(rec, ['crop_removals', element]) ? rec.crop_removals[element] : null;

                if (!crop_removal && rec.previous && rec.yield) {
                    // TODO: do fallback calculation here for when paid recs are loaded initially?
                }

                return crop_removal;
            }
        },
        methods: {
            edit_recs (sample_index) {
                this.$emit('edit_recs', sample_index);
            },
            getUSAState() { // For SW #1313 - Lime recommendations on MyLab (change for Missouri interactive reports)
               return this.$root.selected_account.state_cd;
            }
        },
        mounted () {

        }
    }
</script>



// WEBPACK FOOTER //
// RecommendationsTable.vue?122cf85a